import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { BookOpen, Eye, EyeOff, Mail, Lock, User, AlertCircle, Loader2, Check, X } from 'lucide-react';

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const SignUpPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  
  const { register, isAuthenticated, isLoading: authLoading } = useReaderAuth();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGoogleSignIn = () => {
    if (redirectTo) sessionStorage.setItem('reader_auth_redirect', redirectTo);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/courses';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Password validation rules - simplified
  const passwordRules = [
    { test: (p) => p.length >= 6, label: 'At least 6 characters' },
  ];

  const allRulesPassed = passwordRules.every(rule => rule.test(password));

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      if (redirectTo === 'buy') {
        window.location.href = STRIPE_PAYMENT_LINK;
      } else if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/courses');
      }
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesPassed) return;
    
    setError('');
    setIsSubmitting(true);

    const result = await register(email, password, firstName, lastName);
    
    if (result.success) {
      if (redirectTo === 'buy') {
        window.location.href = STRIPE_PAYMENT_LINK;
      } else if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/courses');
      }
    } else {
      setError(result.error);
    }
    
    setIsSubmitting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center p-6">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-block mb-6">
            <span className="text-[11px] uppercase tracking-[0.28em] font-sans font-semibold" style={{ color: '#071B3A' }}>Third Culture Leadership</span>
          </Link>
          <h1 className="font-serif text-3xl font-light text-navy tracking-tight mb-3">
            Create Your Account
          </h1>
          <p className="text-navy/55 font-sans text-sm font-light">
            Start your cultural fluency learning journey today
          </p>
        </div>

        {/* Register Form */}
        <div className="bg-white p-8 md:p-10" style={{ border: '1px solid rgba(7,27,58,0.1)', boxShadow: '0 2px 24px rgba(7,27,58,0.05)' }}>
          {/* Google Sign-Up */}
          <button
            onClick={handleGoogleSignIn}
            data-testid="google-signup-btn"
            className="w-full flex items-center justify-center gap-3 py-3.5 px-4 transition-all text-navy font-medium text-sm mb-6"
            style={{ border: '1px solid rgba(7,27,58,0.14)', background: 'rgba(7,27,58,0.02)' }}
            onMouseEnter={e => e.currentTarget.style.background='rgba(7,27,58,0.05)'}
            onMouseLeave={e => e.currentTarget.style.background='rgba(7,27,58,0.02)'}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="relative flex items-center my-6">
            <div className="flex-grow border-t" style={{ borderColor: 'rgba(7,27,58,0.1)' }} />
            <span className="mx-4 text-[11px] font-sans uppercase tracking-wider" style={{ color: 'rgba(7,27,58,0.35)' }}>or</span>
            <div className="flex-grow border-t" style={{ borderColor: 'rgba(7,27,58,0.1)' }} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-3 p-4 border text-red-700" style={{ background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.15)' }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2.5" style={{ color: 'rgba(7,27,58,0.55)' }}>
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(7,27,58,0.3)' }} />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 text-navy text-base focus:outline-none transition-all"
                    style={{ background: 'rgba(250,248,243,0.6)', border: '1px solid rgba(7,27,58,0.12)', fontSize: '16px' }}
                    onFocus={e => { e.target.style.borderColor='rgba(201,147,22,0.5)'; e.target.style.background='white'; }}
                    onBlur={e => { e.target.style.borderColor='rgba(7,27,58,0.12)'; e.target.style.background='rgba(250,248,243,0.6)'; }}
                    placeholder="First"
                    required
                    data-testid="signup-firstname"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2.5" style={{ color: 'rgba(7,27,58,0.55)' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3.5 text-navy text-base focus:outline-none transition-all"
                  style={{ background: 'rgba(250,248,243,0.6)', border: '1px solid rgba(7,27,58,0.12)', fontSize: '16px' }}
                  onFocus={e => { e.target.style.borderColor='rgba(201,147,22,0.5)'; e.target.style.background='white'; }}
                  onBlur={e => { e.target.style.borderColor='rgba(7,27,58,0.12)'; e.target.style.background='rgba(250,248,243,0.6)'; }}
                  placeholder="Last"
                  required
                  data-testid="signup-lastname"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2.5" style={{ color: 'rgba(7,27,58,0.55)' }}>
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(7,27,58,0.3)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 text-navy text-base focus:outline-none transition-all"
                  style={{ background: 'rgba(250,248,243,0.6)', border: '1px solid rgba(7,27,58,0.12)', fontSize: '16px' }}
                  onFocus={e => { e.target.style.borderColor='rgba(201,147,22,0.5)'; e.target.style.background='white'; }}
                  onBlur={e => { e.target.style.borderColor='rgba(7,27,58,0.12)'; e.target.style.background='rgba(250,248,243,0.6)'; }}
                  placeholder="you@example.com"
                  required
                  data-testid="signup-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2.5" style={{ color: 'rgba(7,27,58,0.55)' }}>
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(7,27,58,0.3)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3.5 text-navy text-base focus:outline-none transition-all"
                  style={{ background: 'rgba(250,248,243,0.6)', border: '1px solid rgba(7,27,58,0.12)', fontSize: '16px' }}
                  onFocus={e => { e.target.style.borderColor='rgba(201,147,22,0.5)'; e.target.style.background='white'; }}
                  onBlur={e => { e.target.style.borderColor='rgba(7,27,58,0.12)'; e.target.style.background='rgba(250,248,243,0.6)'; }}
                  placeholder="Create a password"
                  required
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(7,27,58,0.35)' }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password Rules */}
            {password && (
              <div className="space-y-1">
                {passwordRules.map((rule, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 text-xs ${
                      rule.test(password) ? 'text-green-600' : 'text-navy/40'
                    }`}
                  >
                    {rule.test(password) ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}
                    {rule.label}
                  </div>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !allRulesPassed}
              className="w-full py-3.5 font-medium text-sm uppercase tracking-[0.15em] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              style={{ background: '#C99316', color: '#071B3A', fontSize: '14px' }}
              data-testid="signup-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account…
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-7 text-center">
            <p className="text-navy/50 text-sm">
              Already have an account?{' '}
              <Link 
                to={`/signin${redirectTo ? `?redirect=${redirectTo}` : ''}`}
                className="text-gold font-medium hover:text-gold/70 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/courses" className="text-[#5a6c7d] text-sm hover:text-[#2c3e50]">
            ← Back to Courses
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
